// Whitelabel palette derivation.
//
// Customers linked to a partner — and partners themselves — get the
// `user.whitelabel` block computed in client/users/get-user. The block
// carries `brand_primary` and `brand_secondary` hex colors plus a `level`
// gate that controls how deep the branding goes (level >= 2 unlocks full
// whitelabel; level 1 stays minimal).
//
// This helper takes a base AppTheme and returns a palette-overlaid copy
// when whitelabel branding is active, or the base theme unchanged when it
// isn't. Mirrors the web theme's branding override logic in
// the legacy web client's themeBuilder so both surfaces
// render the same colors against the same partner.
//
// Surfaces that pick up the override: primary (buttons, links, active nav
// state) plus a low-alpha primary tint on the active nav row. We deliberately
// do NOT repaint the app background with a brand color — the old
// `coreBackground: secondary` swap flooded the whole screen in the partner's
// hue, which swamped the content and read as garish. The brand shows as an
// accent; the background stays the neutral base for legibility.

import type { AppTheme } from './index';

export interface Whitelabel {
  level?: number;
  brand_primary?: string | null;
  brand_secondary?: string | null;
}

const isHex = (v?: string | null): v is string =>
  !!v && /^#[0-9A-Fa-f]{6}$/.test(v);

export const getWhitelabelPalette = (
  baseTheme: AppTheme,
  whitelabel?: Whitelabel | null
): AppTheme => {
  if (!whitelabel) return baseTheme;
  // The brand primary drives the accent (buttons, links, active nav) at every
  // level. brand_secondary is intentionally NOT applied to the app background
  // anymore — see the header note; branding stays an accent, not a full wash.
  const primary = isHex(whitelabel.brand_primary) ? whitelabel.brand_primary : null;
  if (!primary) return baseTheme;

  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      primary,
      // Derived from primary in the base theme, so it must follow the brand.
      primarySubtle: `${primary}15`,
      // A brand primary replaces the darker coral for filled controls too.
      primaryStrong: primary,
      // sidebarActive picks up a translucent variant of the primary so the
      // active-nav row tints toward the partner color without stealing
      // attention from the active item. Keep alpha low (0.16) so it
      // matches the existing sidebarActive intensity.
      sidebarActive: hexToRgba(primary, 0.16),
    },
  };
};

// Tiny inline hex → rgba so we don't pull in a color helper. Falls back
// to the original input on any parse error so a malformed brand_primary
// can never crash the theme provider.
const hexToRgba = (hex: string, alpha: number): string => {
  const m = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
};
